import BigNumber from 'bignumber.js'
const MAX_LIMIT = new BigNumber(99999999999)

// Класс для работы с комиссиями и лимитами
class TransferCalculation {
    constructor(data) {
        data = data || {}

        // Валюты
        this.from_currency_code = data.from_currency_code || null
        this.to_currency_code = data.to_currency_code || null

        // Курс
        this.rate = {
            value: new BigNumber(0),
            get: new BigNumber(0),
            send: new BigNumber(0),
            limits: { 
                min: new BigNumber(0), 
                max: new BigNumber(0), 
                free: new BigNumber(0) 
            },
            get_bank_codes: [],
            send_bank_codes: [],
            get_banks: [],
            send_banks: [],
        }

        // Отправитель и получатель
        this.sender = {
            amount: new BigNumber(0),
            amount_usdt: new BigNumber(0)
        }
        this.recipient = {
            amount: new BigNumber(0),
            amount_usdt: new BigNumber(0)
        }

        // Платежные системы
        this.pay_systems = {
            get: {
                amount: new BigNumber(0),
                limits: { 
                    min: new BigNumber(0), 
                    max: MAX_LIMIT
                },
                coms: { 
                    percent: new BigNumber(0), 
                    fix: new BigNumber(0) 
                },
            },
            send: {
                amount: new BigNumber(0),
                limits: { 
                    min: new BigNumber(0), 
                    max: MAX_LIMIT
                },
                coms: { 
                    percent: new BigNumber(0), 
                    fix: new BigNumber(0) 
                },
            }
        }

        // Банки
        this.banks = {
            get: {
                amount: new BigNumber(0),
                limits: { 
                    min: new BigNumber(0), 
                    max: MAX_LIMIT
                },
                coms: { 
                    percent: new BigNumber(0), 
                    fix: new BigNumber(0) 
                },
                code: null,
                type: null
            },
            send: {
                amount: new BigNumber(0),
                limits: { 
                    min: new BigNumber(0), 
                    max: MAX_LIMIT
                },
                coms: { 
                    percent: new BigNumber(0), 
                    fix: new BigNumber(0) 
                },
                code: null,
                type: null
            }
        }

        // Мерчанты
        this.merchants = {
            get: {
                amount: new BigNumber(0),
                percent: new BigNumber(0),
                fix: new BigNumber(0)
            },
            send: {
                amount: new BigNumber(0),
                percent: new BigNumber(0),
                fix: new BigNumber(0)
            }
        }

        // Сервис
        this.service = {
            percent: new BigNumber(0),
            fix: new BigNumber(0),
            amount: new BigNumber(0)
        }

        // Партнер
        this.partner = {
            balance: new BigNumber(0),
            percent: new BigNumber(0),
            amount: new BigNumber(0)
        }
    }

    // Получение положительного BigNumber из значения, возвращает 0, если значение отрицательное или не число
    getPositiveBigNumber(value) {
        const amount = new BigNumber(value || 0)
        if(amount.isNaN() || amount.isNegative()) return new BigNumber(0)
        return amount
    }

    // Установка кешбэка партнера
    setPartnerCashback(balance) {
        this.partner.balance = this.getPositiveBigNumber(balance)
        this.partner.amount = new BigNumber(0)
    }

    // Расчет кешбэка партнера
    calcPartnerCashback(grossComissions, free=false) {
        this.partner.amount = new BigNumber(0)
        if(free) return new BigNumber(0)

        const comissions = this.getPositiveBigNumber(grossComissions)
        if(comissions.isZero() || this.partner.balance.isZero()) return new BigNumber(0)

        this.partner.amount = BigNumber.minimum(this.partner.balance, comissions)
        return this.partner.amount
    }

    // Расчет валовых комиссий по сумме отправителя
    calcGrossComissionsBySenderAmountUsdt(senderAmountUsdt) {
        const amount = this.getPositiveBigNumber(senderAmountUsdt)

        const comsService = amount.multipliedBy(this.service.percent)
        const comsGetPaySys = amount.multipliedBy(this.pay_systems.get.coms.percent).plus(this.pay_systems.get.coms.fix)
        const comsSendPaySys = amount.multipliedBy(this.pay_systems.send.coms.percent).plus(this.pay_systems.send.coms.fix)
        const comsGetMerchant = amount.multipliedBy(this.merchants.get.percent)
        const comsSendMerchant = amount.multipliedBy(this.merchants.send.percent)
        const comsSendBank = amount.multipliedBy(this.banks.send.coms.percent).plus(this.banks.send.coms.fix)

        return comsService.plus(comsGetPaySys).plus(comsSendPaySys).plus(comsGetMerchant).plus(comsSendMerchant).plus(comsSendBank)
    }

    getFreeTransferSendLimit() {
        if(!this.rate.limits.free || this.rate.limits.free.isZero() || this.rate.limits.free.isNaN()) return new BigNumber(0)
        return this.rate.limits.free
    }

    // Установка объекта курса
    setRate(rateObj, type = 'transfer') {
        if (!rateObj || typeof rateObj !== 'object') return

        // Курсы
        if (rateObj.get) this.rate.get = new BigNumber(rateObj.get)
        if (rateObj.send) this.rate.send = new BigNumber(rateObj.send)

        // Лимиты
        if (rateObj.limits && rateObj.limits[type]) {
            this.rate.limits.min = new BigNumber(rateObj.limits[type].min)
            this.rate.limits.max = new BigNumber(rateObj.limits[type].max)
            this.rate.limits.free = new BigNumber(rateObj.limits[type].free)
        }

        // Банки
        if (rateObj.get_bank_codes) this.rate.get_bank_codes = rateObj.get_bank_codes
        if (rateObj.send_bank_codes) this.rate.send_bank_codes = rateObj.send_bank_codes

        if (rateObj.get_banks) this.rate.get_banks = rateObj.get_banks
        if (rateObj.send_banks) this.rate.send_banks = rateObj.send_banks

        if(rateObj.comissions && rateObj.comissions[type]) {
            // Сервис
            this.service.percent =      (rateObj.comissions[type].service && rateObj.comissions[type].service.percent) ? new BigNumber(rateObj.comissions[type].service.percent) : new BigNumber(0)
            this.service.fix =          (rateObj.comissions[type].service && rateObj.comissions[type].service.fix) ? new BigNumber(rateObj.comissions[type].service.fix) : new BigNumber(0)

            // Мерчанты
            this.merchants.get.percent= (rateObj.comissions[type].get && rateObj.comissions[type].get.percent) ? new BigNumber(rateObj.comissions[type].get.percent) : new BigNumber(0)
            this.merchants.get.fix =    (rateObj.comissions[type].get && rateObj.comissions[type].get.fix) ? new BigNumber(rateObj.comissions[type].get.fix) : new BigNumber(0)
            this.merchants.send.percent=(rateObj.comissions[type].send && rateObj.comissions[type].send.percent) ? new BigNumber(rateObj.comissions[type].send.percent) : new BigNumber(0)
            this.merchants.send.fix =   (rateObj.comissions[type].send && rateObj.comissions[type].send.fix) ? new BigNumber(rateObj.comissions[type].send.fix) : new BigNumber(0)
        }
    }

    // Расчет итогового курса
    calcResultRate(){
        // Считаем итоговый курс как recipient / sender
        if(this.sender.amount.isZero()) {
            this.rate.value = new BigNumber(0)
            return
        }
        const rateValue = this.recipient.amount.dividedBy(this.sender.amount)
        this.rate.value = rateValue
    }

    // Установка комиссии платежной системы приема
    setGetPaySys(pay_sys) {
        if (!pay_sys || typeof pay_sys !== 'object') return
        // комиссии платежной системы приема
        if (pay_sys.comissions) {
            if (pay_sys.comissions.percent) this.pay_systems.get.coms.percent = new BigNumber(pay_sys.comissions.percent)
            if (pay_sys.comissions.fix) this.pay_systems.get.coms.fix = new BigNumber(pay_sys.comissions.fix)
        }
        // лимиты платежной системы приема
        if (pay_sys.limits) {
            if (pay_sys.limits.min) this.pay_systems.get.limits.min = new BigNumber(pay_sys.limits.min)
            if (pay_sys.limits.max) this.pay_systems.get.limits.max = new BigNumber(pay_sys.limits.max)
        }
    }

    // Установка комиссии платежной системы отправки
    setSendPaySys(pay_sys) {
        if (!pay_sys || typeof pay_sys !== 'object') return
        // комиссии платежной системы выплаты
        if (pay_sys.comissions) {
            if (pay_sys.comissions.percent) this.pay_systems.send.coms.percent = new BigNumber(pay_sys.comissions.percent)
            if (pay_sys.comissions.fix) this.pay_systems.send.coms.fix = new BigNumber(pay_sys.comissions.fix)
        }
        // лимиты платежной системы выплаты
        if (pay_sys.limits) {
            if (pay_sys.limits.min) this.pay_systems.send.limits.min = new BigNumber(pay_sys.limits.min)
            if (pay_sys.limits.max) this.pay_systems.send.limits.max = new BigNumber(pay_sys.limits.max)
        }
    }

    // Установка банка приема
    setGetBank(bank) {
        if (!bank || typeof bank !== 'object') return
        // комиссии банка приема
        if (bank.comissions) {
            if (bank.comissions.percent) this.banks.get.coms.percent = new BigNumber(bank.comissions.percent)
            if (bank.comissions.fix) this.banks.get.coms.fix = new BigNumber(bank.comissions.fix)
        }
        // лимиты банка приема
        if (bank.limits) {
            if (bank.limits.min) this.banks.get.limits.min = new BigNumber(bank.limits.min)
            if (bank.limits.max) this.banks.get.limits.max = new BigNumber(bank.limits.max)
        }
        if(bank.code) this.banks.get.code = bank.code
        if(bank.type) this.banks.get.type = bank.type
    }

    // Установка банка отправки
    setSendBank(bank) {
        if (!bank || typeof bank !== 'object') return
        // комиссии банка отправки
        if (bank.comissions) {
            if (bank.comissions.percent) this.banks.send.coms.percent = new BigNumber(bank.comissions.percent)
            if (bank.comissions.fix) this.banks.send.coms.fix = new BigNumber(bank.comissions.fix)
        }
        // лимиты банка отправки
        if (bank.limits) {
            if (bank.limits.min) this.banks.send.limits.min = new BigNumber(bank.limits.min)
            if (bank.limits.max) this.banks.send.limits.max = new BigNumber(bank.limits.max)
        }
        if(bank.code) this.banks.send.code = bank.code
        if(bank.type) this.banks.send.type = bank.type
    }

    // Установка платежной системы типа get или send
    setPaymentSystem(data, type) {
        if (!type || (type !== 'get' && type !== 'send')) return
        if (!data || typeof data !== 'object') return
        if (type === 'get') {
            if (data.percent) this.pay_systems.get.coms.percent = new BigNumber(data.percent)
            if (data.fix) this.pay_systems.get.coms.fix = new BigNumber(data.fix)
        } else if (type === 'send') {
            if (data.percent) this.pay_systems.send.coms.percent = new BigNumber(data.percent)
            if (data.fix) this.pay_systems.send.coms.fix = new BigNumber(data.fix)
        }
    }

    // Лимит получения (большее из значений)
    // ВАЖНО: перед вызовом этого метода должны быть установлены банки и платежные системы
    getMinGetLimit() {
        const pay_sys = this.pay_systems.get
        const bank = this.banks.get
        const bankCode = (bank.code) ? bank.code : null
        const bankType = (bank.type) ? bank.type : null
        let minLimit = new BigNumber(0)

        // 1 - лимит от курса
        if (this.rate.limits.min && this.rate.limits.min.gt(minLimit)) {
            minLimit = this.rate.limits.min
        }

        // 2 - лимит от платежной системы
        if (pay_sys && pay_sys.limits && pay_sys.limits.min) {
            let paySysMinLimit = new BigNumber(pay_sys.limits.min)
            if (paySysMinLimit.gt(minLimit)) {
                minLimit = paySysMinLimit
            }
        }

        // 3 - лимит от банка
        if (bank && bank.limits && bank.limits.min) {
            let bankMinLimit = bank.limits.min
            if (bankMinLimit.gt(minLimit)) {
                minLimit = bankMinLimit
            }
        }

        // 4 - лимит банков из курса get_banks, send_banks
        if (bank && bankCode && bankType) {
            const getBanks = this.rate.get_banks || []
            if(!getBanks || !Array.isArray(getBanks)) return minLimit.toString()

            for(let i = 0; i < getBanks.length; i++){
                if(getBanks[i].code !== bankCode || getBanks[i].type !== bankType) continue
                if(getBanks[i].min){
                    let bankMinLimit = new BigNumber(getBanks[i].min)
                    if (bankMinLimit.gt(minLimit)) minLimit = bankMinLimit
                }
            }
        }

        return minLimit.toString()
    }

    // Лимит получения (меньшее из значений, но не больше максимального лимита курса)
    // ВАЖНО: перед вызовом этого метода должны быть установлены банки и платежные системы
    getMaxGetLimit() {
        const pay_sys = this.pay_systems.get
        const bank = this.banks.get
        const bankCode = (bank.code) ? bank.code : null
        const bankType = (bank.type) ? bank.type : null
        let maxLimit = MAX_LIMIT

        // 1 - лимит от курса
        if (this.rate.limits.max && this.rate.limits.max.lt(maxLimit)) {
            maxLimit = this.rate.limits.max
        }

        // 2 - лимит от платежной системы
        if (pay_sys && pay_sys.limits && pay_sys.limits.max) {
            let paySysMaxLimit = new BigNumber(pay_sys.limits.max)
            if (paySysMaxLimit.lt(maxLimit)) {
                maxLimit = paySysMaxLimit
            }
        }

        // 3 - лимит от банка
        if (bank && bank.limits && bank.limits.max) {
            let bankMaxLimit = bank.limits.max
            if (bankMaxLimit.lt(maxLimit)) {
                maxLimit = bankMaxLimit
            }
        }

        // 4 - лимит банков из курса get_banks, send_banks
        if (bank && bankCode && bankType) {
            const getBanks = this.rate.get_banks || []
            if(!getBanks || !Array.isArray(getBanks)) return maxLimit.toString()
            for(let i = 0; i < getBanks.length; i++){
                if(getBanks[i].code !== bankCode || getBanks[i].type !== bankType) continue
                if(getBanks[i].max){
                    let bankMaxLimit = new BigNumber(getBanks[i].max)
                    if (bankMaxLimit.lt(maxLimit)) maxLimit = bankMaxLimit
                }
            }
        }

        return maxLimit.toString()
    }

    // Лимит отправки (меньшее из значений, но не больше максимального лимита курса)
    getMaxSendLimit(pay_sys, bank_code = null) {
        let maxLimit = MAX_LIMIT

        // 1 - лимит от курса
        if (this.rate.limits.max && this.rate.limits.max.isGreaterThan(0)) {
            maxLimit = this.rate.limits.max
        }

        // 2 - лимит от платежной системы
        if (pay_sys && pay_sys.limits && pay_sys.limits.max) {
            let paySysMaxLimit = new BigNumber(pay_sys.limits.max)
            if (paySysMaxLimit.isLessThan(maxLimit)) maxLimit = paySysMaxLimit
        }

        // 3 - лимит от банка (новая структура)
        if (bank_code && this.banks.send && this.banks.send.limits && this.banks.send.limits.max) {
            let bankMaxLimit = this.banks.send.limits.max
            if (bankMaxLimit.isLessThan(maxLimit)) maxLimit = bankMaxLimit
        }
        return maxLimit.toString()
    }

    // Расчет суммы получения в валюте получателя по сумме отправки в валюте отправителя
    calcRecipientAmountBySenderAmount(senderAmount, free=false) {
        if (!senderAmount || senderAmount.isZero()) return new BigNumber(0);
        if (this.rate.get.isZero() || this.rate.send.isZero()) return new BigNumber(0);

        // Сумма отправки в USDT
        const senderAmountUsdt = senderAmount.multipliedBy(this.rate.get);

        // Кешбек уменьшает только комиссию, но не сумму перевода.
        const grossComs = this.calcGrossComissionsBySenderAmountUsdt(senderAmountUsdt)
        const totalComs = (free) ? new BigNumber(0) : grossComs.minus(this.calcPartnerCashback(grossComs, free))

        // Сумма получения в USDT
        const recipientAmountUsdt = senderAmountUsdt.minus(totalComs);
        const recipientAmount = recipientAmountUsdt.multipliedBy(this.rate.send);
        return recipientAmount;
    }

    // Фильтрация массива сумм по лимитам
    filterAmountListByLimits(amountList, minLimit, maxLimit) {
        if (!amountList || !Array.isArray(amountList) || amountList.length == 0) return []
        const filteredList = amountList.filter(item => {
            const amount = new BigNumber(item)
            if (amount.isLessThan(minLimit)) return false
            if (amount.isGreaterThan(maxLimit)) return false
            return true
        })  
        return filteredList
    }

    // Расчет суммы отправки в валюте отправителя
    calcSenderAmount(free=false) {
        if (!this.recipient.amount || this.recipient.amount.isZero()) return new BigNumber(0)
        if (this.rate.send.isZero()) return new BigNumber(0)
        const senderAmountUsdt = this.recipient.amount.dividedBy(this.rate.send)
        const totalComs = this.calcAllComissionsByRecipientAmount(free)
        const totalAmountUsdt = senderAmountUsdt.plus(totalComs)
        const senderAmount = totalAmountUsdt.dividedBy(this.rate.get)
        return senderAmount
    }

    // Расчет суммы отправки в USDT
    calcSenderAmountUsdt() {
        if (!this.sender.amount || this.sender.amount.isZero()) return new BigNumber(0);
        if (this.rate.get.isZero()) return new BigNumber(0);
        const senderAmountUsdt = this.sender.amount.multipliedBy(this.rate.get);
        return senderAmountUsdt;
    }

    // Сумма всех комиссий (фикс + процент) от суммы отправки в USDT
    calcAllComissions(free=false) {
        if (!this.sender.amount || this.sender.amount.isZero() || this.rate.get.isZero()) return new BigNumber(0);
        const senderAmountUsdt = this.sender.amount.multipliedBy(this.rate.get);

        const grossComs = this.calcGrossComissionsBySenderAmountUsdt(senderAmountUsdt)
        if(free) {
            this.partner.amount = new BigNumber(0)
            return new BigNumber(0)
        }
        return grossComs.minus(this.calcPartnerCashback(grossComs, free));
    }

    // Возвращает сумму всех комиссий, если известна сумма получения (this.recipient.amount)
    calcAllComissionsByRecipientAmount(free=false) {
        if (!this.recipient.amount || this.recipient.amount.isZero()) return new BigNumber(0);
        if (this.rate.send.isZero() || this.rate.get.isZero()) return new BigNumber(0);
        
        // Суммируем проценты (service, get_pay_sys, send_pay_sys, get_merchant, send_merchant, send_bank)
        const percentSum = this.service.percent
            .plus(this.pay_systems.get.coms.percent)
            .plus(this.pay_systems.send.coms.percent)
            .plus(this.merchants.get.percent)
            .plus(this.merchants.send.percent)
            .plus(this.banks.send.coms.percent)

        // Суммируем все фиксированные комиссии (get_pay_sys, send_pay_sys, send_bank)
        const fixSum = this.pay_systems.get.coms.fix
            .plus(this.pay_systems.send.coms.fix)
            .plus(this.banks.send.coms.fix);

        // Сумма, которую должен получить получатель (в валюте получения)
        const amountReceiveUsdt = this.recipient.amount.dividedBy(this.rate.send);

        // Сумма, которую нужно отправить в USDT с учетом кешбека
        // X = max(A, (A + F - C) / (1 - P))
        const one = new BigNumber(1);
        const denominator = one.minus(percentSum);
        if (denominator.isZero() || denominator.isNegative()) return new BigNumber(0);
        const amountSendWithCashback = amountReceiveUsdt.plus(fixSum).minus(this.partner.balance).dividedBy(denominator);
        const amountSend = BigNumber.maximum(amountReceiveUsdt, amountSendWithCashback);

        const grossComs = this.calcGrossComissionsBySenderAmountUsdt(amountSend)
        if(free) {
            this.partner.amount = new BigNumber(0)
            return new BigNumber(0)
        }

        const cashback = this.calcPartnerCashback(grossComs, free)
        const totalComs = grossComs.minus(cashback)
        return BigNumber.maximum(new BigNumber(0), totalComs);
    }

    // Сумма получения в USDT с учетом всех комиссий
    calcRecipientAmountUsdt(free=false) {
        if (!this.sender.amount || this.sender.amount.isZero()) return new BigNumber(0);
        if (this.rate.get.isZero() || this.rate.send.isZero()) return new BigNumber(0);

        // Сумма отправки в USDT
        const senderAmountUsdt = this.sender.amount.multipliedBy(this.rate.get)

        // Общая сумма комиссий
        const totalComs = this.calcAllComissions(free);

        const result = senderAmountUsdt.minus(totalComs);
        return result;
    }

    // Сумма получения в валюте получателя
    calcRecipientAmount() {
        if (!this.sender.amount_usdt || this.recipient.amount_usdt.isZero()) {
            this.sender.amount_usdt = this.calcSenderAmountUsdt();
        }
        if (this.rate.send.isZero()) return new BigNumber(0);
        const recipientAmount = this.recipient.amount_usdt.multipliedBy(this.rate.send);
        return recipientAmount;
    }

    // Учет внутреннего баланса
    static normalizeInternalPaySystems(list, options = {}) {
        if(!Array.isArray(list) || !list.length) return []

        const depositWalletId = options.depositWalletId || null
        const depositBalance = options.depositBalance || 0
        const hasDepositWallet = Boolean(depositWalletId)

        if(!hasDepositWallet){
            return list.filter(item => !(item && item.pay_sys && item.pay_sys.type === 'internal'))
        }

        const balance = new BigNumber(depositBalance)
        const maxLimit = (balance.isNaN() || balance.isNegative()) ? new BigNumber(0) : balance
        const limitValue = maxLimit.toString()

        return list.reduce((acc, item) => {
            if(!item || !item.pay_sys) return acc
            if(item.pay_sys.type !== 'internal'){
                acc.push(item)
                return acc
            }

            let minRequired = new BigNumber(0)
            if(item.pay_sys.limits && item.pay_sys.limits.min !== undefined && item.pay_sys.limits.min !== null){
                const candidate = new BigNumber(item.pay_sys.limits.min)
                if(!candidate.isNaN() && candidate.isGreaterThan(minRequired)) minRequired = candidate
            }
            if(item.bank && item.bank.limits && item.bank.limits.min !== undefined && item.bank.limits.min !== null){
                const candidate = new BigNumber(item.bank.limits.min)
                if(!candidate.isNaN() && candidate.isGreaterThan(minRequired)) minRequired = candidate
            }

            if(maxLimit.isLessThan(minRequired)) return acc

            const cloned = {
                ...item,
                pay_sys: { ...item.pay_sys }
            }

            if(item.bank){
                const bankClone = { ...item.bank }
                if(item.bank.limits) bankClone.limits = { ...item.bank.limits }
                cloned.bank = bankClone
            }

            if(cloned.pay_sys.code === 'internal_withdraw'){
                if(cloned.pay_sys.limits) cloned.pay_sys.limits = { ...cloned.pay_sys.limits, max: limitValue }
                else cloned.pay_sys.limits = { min: 0, max: limitValue }

                if(cloned.bank){
                    if(cloned.bank.limits) cloned.bank.limits = { ...cloned.bank.limits, max: limitValue }
                    else cloned.bank.limits = { min: 0, max: limitValue }
                }
            }

            acc.push(cloned)
            return acc
        }, [])
    }

    toObject() {
        return {
            from_currency_code: this.from_currency_code,
            to_currency_code: this.to_currency_code,
            rate: {
                value: this.rate.value.toString(),
                get: this.rate.get.toString(),
                send: this.rate.send.toString(),
                limits: {
                    min: this.rate.limits.min.toString(),
                    max: this.rate.limits.max.toString(),
                    free: this.rate.limits.free.toString()
                },
                get_bank_codes: this.rate.get_bank_codes,
                send_bank_codes: this.rate.send_bank_codes
            },
            sender: {
                amount: this.sender.amount.toString(),
                amount_usdt: this.sender.amount_usdt.toString()
            },
            recipient: {
                amount: this.recipient.amount.toString(),
                amount_usdt: this.recipient.amount_usdt.toString()
            },
            pay_systems: {
                get: {
                    amount: this.pay_systems.get.amount.toString(),
                    limits: {
                        min: this.pay_systems.get.limits.min.toString(),
                        max: this.pay_systems.get.limits.max.toString()
                    },
                    coms: {
                        percent: this.pay_systems.get.coms.percent.toString(),
                        fix: this.pay_systems.get.coms.fix.toString()
                    }
                },
                send: {
                    amount: this.pay_systems.send.amount.toString(),
                    limits: {
                        min: this.pay_systems.send.limits.min.toString(),
                        max: this.pay_systems.send.limits.max.toString()
                    },
                    coms: {
                        percent: this.pay_systems.send.coms.percent.toString(),
                        fix: this.pay_systems.send.coms.fix.toString()
                    }
                }
            },
            banks: {
                get: {
                    amount: this.banks.get.amount.toString(),
                    limits: {
                        min: this.banks.get.limits.min.toString(),
                        max: this.banks.get.limits.max.toString()
                    },
                    coms: {
                        percent: this.banks.get.coms.percent.toString(),
                        fix: this.banks.get.coms.fix.toString()
                    }
                },
                send: {
                    amount: this.banks.send.amount.toString(),
                    limits: {
                        min: this.banks.send.limits.min.toString(),
                        max: this.banks.send.limits.max.toString()
                    },
                    coms: {
                        percent: this.banks.send.coms.percent.toString(),
                        fix: this.banks.send.coms.fix.toString()
                    }
                }
            },
            merchants: {
                get: {
                    amount: this.merchants.get.amount.toString(),
                    percent: this.merchants.get.percent.toString(),
                    fix: this.merchants.get.fix.toString()
                },
                send: {
                    amount: this.merchants.send.amount.toString(),
                    percent: this.merchants.send.percent.toString(),
                    fix: this.merchants.send.fix.toString()
                }
            },
            service: {
                amount: this.service.amount.toString(),
                percent: this.service.percent.toString(),
                fix: this.service.fix.toString()
            },
            partner: {
                balance: this.partner.balance.toString(),
                percent: this.partner.percent.toString(),
                amount: this.partner.amount.toString()
            },
        };
    }
}

export default TransferCalculation